import { useEffect, useRef } from 'react';

import type {
  SelectionList,
  TreeItem,
  ValidValueTypes,
} from '@revelio/iso-utility';

import { updateReportEntitySelections } from '../reports/report-builder/02-entity-configuration/mw-reports/report-entity-selection.utils';
import type { IReportEntityMap } from '../reports/report-builder/02-entity-configuration/mw-reports/utils';
import type { ReportBuilderDeliverable } from '../reports/report-builder/report-builder.model';
import {
  globalAssistantReportLaunchPendingStore,
  useGlobalAssistantReportLaunchPendingStore,
} from './global-ai-assistant-report-launch-pending';
import { getReportParameterMapping } from './global-ai-assistant-report-parameter-catalog';
import {
  toMwCustomSkillTreeSelectionsFromResolved,
  usesMwCustomSkillSelectionLists,
} from './global-ai-assistant-report-resolved-selection';

const findMwSkillsColumn = (
  reportEntityMap: IReportEntityMap,
  reportType: string
): { columnId: string; selectionLists: string[] } | null => {
  const mapping = getReportParameterMapping(reportType, 'skills');
  if (!mapping) {
    return null;
  }

  const columns = reportEntityMap[reportType]?.columns ?? [];
  const column = columns.find((entry) =>
    mapping.mwColumnLabels.some((label) => entry.label === label)
  );
  if (!column?.id) {
    return null;
  }

  return {
    columnId: column.id,
    selectionLists:
      column.reportEntityOptions.selectionList.filters.map(String),
  };
};

export const useGlobalAssistantMwReportSkillPrefill = ({
  reportId,
  reportType,
  reportEntityMap,
  customSkillLists,
  customSkillListsLoading,
}: {
  reportId: ReportBuilderDeliverable['id'] | undefined;
  reportType: string | undefined;
  reportEntityMap: IReportEntityMap;
  customSkillLists: SelectionList<ValidValueTypes>[];
  customSkillListsLoading: boolean;
}) => {
  const mwPrefillSuggestions = useGlobalAssistantReportLaunchPendingStore(
    (state) => state.mwPrefillSuggestions
  );
  const mwPrefillLaunchSeq = useGlobalAssistantReportLaunchPendingStore(
    (state) => state.mwPrefillLaunchSeq
  );
  const fulfilledLaunchSeqRef = useRef<number | null>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: re-run when custom skill lists finish loading so we can abandon stale MW prefill
  useEffect(() => {
    if (
      reportId === null ||
      reportId === undefined ||
      !reportType ||
      mwPrefillSuggestions.length === 0 ||
      customSkillLists.length === 0
    ) {
      return;
    }

    if (fulfilledLaunchSeqRef.current === mwPrefillLaunchSeq) {
      return;
    }

    const abandonMwPrefill = () => {
      fulfilledLaunchSeqRef.current = mwPrefillLaunchSeq;
      globalAssistantReportLaunchPendingStore
        .getState()
        .actions.clearMwReportPrefill();
    };

    const skillsColumn = findMwSkillsColumn(reportEntityMap, reportType);
    if (
      !skillsColumn ||
      !usesMwCustomSkillSelectionLists(skillsColumn.selectionLists)
    ) {
      abandonMwPrefill();
      return;
    }

    const skillSuggestions = mwPrefillSuggestions.filter(
      (suggestion) =>
        suggestion.parameterId === 'skills' &&
        suggestion.resolutionStatus === 'resolved' &&
        suggestion.resolvedSelection
    );
    if (skillSuggestions.length === 0) {
      abandonMwPrefill();
      return;
    }

    const mergedSelections = skillSuggestions.reduce<
      Record<string, TreeItem<string>>
    >((acc, suggestion) => {
      if (!suggestion.resolvedSelection) {
        return acc;
      }
      return {
        ...acc,
        ...toMwCustomSkillTreeSelectionsFromResolved(
          suggestion.resolvedSelection,
          customSkillLists
        ),
      };
    }, {});

    if (Object.keys(mergedSelections).length === 0) {
      abandonMwPrefill();
      return;
    }

    updateReportEntitySelections({
      reportId,
      reportEntity: skillsColumn.columnId,
      selections: mergedSelections,
    });

    fulfilledLaunchSeqRef.current = mwPrefillLaunchSeq;
    globalAssistantReportLaunchPendingStore
      .getState()
      .actions.clearMwReportPrefill();
  }, [
    customSkillLists,
    customSkillListsLoading,
    mwPrefillLaunchSeq,
    mwPrefillSuggestions,
    reportEntityMap,
    reportId,
    reportType,
  ]);
};
